// frontend/src/riskEngine.js
import { RISK_THRESHOLDS, SECTION_ANSWER_POINTS } from "./riskSchema.js";

/**
 * Build section points map from schema (if present) or fall back to A/B defaults.
 */
function buildSectionPoints(schema) {
  // Fallbacks reflect your required behavior:
  // A: Y=0, N=1, NA=0  |  B: Y=1, N=0, NA=0
  const fallback = SECTION_ANSWER_POINTS;
  const sp = (schema && schema.ANSWER_POINTS) || {};
  return {
    A: { ...fallback.A, ...(sp.A || {}) },
    B: { ...fallback.B, ...(sp.B || {}) },
  };
}

/**
 * Compute total + per-section scores and normalized averages.
 * questions: array of { id, section, weight? }
 * answers: { QID: "Y"|"N"|"NA" }
 */
export function computeScore({ answers = {}, questions = [], schema = null, thresholds = RISK_THRESHOLDS }) {
  const sectionPoints = buildSectionPoints(schema);
  const bySection = { A: 0, B: 0 };
  const maxBySection = { A: 0, B: 0 };

  for (const q of questions) {
    const section = q.section;
    if (!section || !sectionPoints[section]) continue;

    const raw = String(answers[q.id] ?? "").toUpperCase();
    const code = raw === "NA" ? "NA" : raw[0]; // "Y" | "N" | "NA"
    const pts = Number(sectionPoints[section][code] ?? 0);
    const w = Number(q.weight || 1);

    bySection[section] += pts * w;

    const secMax = Math.max(...Object.values(sectionPoints[section]).map(Number));
    maxBySection[section] += secMax * w;
  }

  const total = (bySection.A || 0) + (bySection.B || 0);
  const maxTotal = (maxBySection.A || 0) + (maxBySection.B || 0);

  const avgBySection = {
    A: maxBySection.A ? bySection.A / maxBySection.A : 0,
    B: maxBySection.B ? bySection.B / maxBySection.B : 0,
  };
  const avgOverall = maxTotal ? total / maxTotal : 0;

  // risk level from canonical thresholds
  const t = thresholds || RISK_THRESHOLDS;
  let level = "low";
  if (total >= t.very_high.min) level = "very_high";
  else if (total >= t.high.min) level = "high";
  else if (total >= t.medium.min) level = "medium";

  return { total, bySection, maxBySection, avgBySection, avgOverall, level };
}

/**
 * Map internal level key -> API label
 * API expects: "low" | "medium" | "high" | "very high" (with a space)
 */
export function levelToApiLabel(levelKey) {
  if (levelKey === "very_high") return "very high";
  return String(levelKey || "").toLowerCase();
}

export function computeRisk(args) {
  const result = computeScore(args);
  const apiLabel = levelToApiLabel(result.level);
  return {
    ...result,
    risk_level: apiLabel, // ← Add this for backward compatibility
    risk_level_api: apiLabel, // ← Keep this for API consistency
  };
}
